import React from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import ErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import cx from 'classnames'
import * as R from 'ramda'

import { InitialStatePlugin } from 'components/shared/FormFields/common/Editors/plugins/InitialStatePlugin/InitialStatePlugin'
import { VariablesButtonSetPlugin } from 'components/shared/FormFields/common/Editors/plugins/VariablesButtonSetPlugin/VariablesButtonSetPlugin'
import { TextVariablePlugin } from 'components/shared/FormFields/common/Editors/plugins/TextVariablePlugin/TextVariablePlugin'

import { TextVariableNode } from 'components/shared/FormFields/common/Editors/nodes/TextVariableNode/TextVariableNode'

import type { MessageTemplateEditorProps } from 'components/shared/FormFields/common/Editors/types'
import styles from './MessageTemplateEditor.module.scss'

const theme = {
  paragraph: styles.paragraph,
  variable: styles.variable,
}

export const MessageTemplateEditor = React.forwardRef<
  HTMLDivElement,
  MessageTemplateEditorProps
>(
  (
    {
      error,
      id,
      name,
      placeholder,
      value,
      variables,
      onBlur,
      onChange,
      isDisabled,
      isRequired,
    },
    ref,
  ) => {
    const initialConfig = {
      namespace: 'MessageTemplateEditor',
      theme,
      onError: error => {
        console.log({ error })
      },
      editorState: null,
      nodes: [TextVariableNode],
    }

    const handleChange = React.useCallback(
      editorState => {
        const state = editorState.toJSON()?.root?.children[0]?.children

        editorState.read(() => {
          onChange?.(
            R.pipe(
              R.reduce(
                (acc, { id, text, type }) =>
                  acc +
                  (type === 'linebreak'
                    ? '\n'
                    : type === 'variable'
                      ? `{{ ${id} }}`
                      : text),
                '',
              ),
              R.trim,
            )(state),
          )
        })
      },
      [onChange],
    )

    return (
      <LexicalComposer {...{ initialConfig }}>
        <VariablesButtonSetPlugin {...{ variables }} />
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className={cx(styles.editor, {
                [styles.invalid]: Boolean(error),
              })}
              disabled={isDisabled}
              required={isRequired}
              {...{ id, name, onBlur }}
            />
          }
          placeholder={placeholder ? <span>{placeholder}</span> : null}
          ErrorBoundary={ErrorBoundary}
        />
        <InitialStatePlugin {...{ value, variables }} />
        <OnChangePlugin onChange={handleChange} />
        <TextVariablePlugin {...{ variables }} />
      </LexicalComposer>
    )
  },
)
